import { get, post, patch, del } from '../httpMethods';
import httpService from '../httpService';
import { ApiResponse, PaginationMeta } from '../../types/api';

export type ReferralPartnerType = 'DOCTOR' | 'EMPLOYEE' | 'OTHER';

export interface ReferralPartner {
  id: string;
  code: string;
  type: ReferralPartnerType;
  employeeId?: string | null;
  employee?: { id: string; name?: string; firstName?: string; lastName?: string; employeeCode?: string; type?: string } | null;
  name: string;
  mobileNumber?: string;
  email?: string;
  organization?: string;
  designation?: string;
  specialization?: string;
  address?: string;
  /** The partner's default referral share %; overridable per receipt. */
  defaultSharePercent: number;
  isActive: boolean;
  notes?: string;
  bankName?: string;
  bankAccountNumber?: string;
  bankBranch?: string;
  bankRoutingNumber?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Set when the partner is in the trash (soft-deleted). */
  deletedAt?: string | null;
}

export interface ReferralSummary {
  referralId: string;
  pendingShare: number;
  postedShare: number;
  settledShare: number;
  lifetimeRevenue: number;
  distributionCount: number;
  payoutCount: number;
  paidAmount: number;
}

export interface ReferralPayout {
  id: string;
  paymentNumber: string;
  amount: number;
  method?: string;
  status: string;
  paidAt: string;
  distributionCount?: number;
}

export interface QuickReferralPartnerInput {
  type?: ReferralPartnerType;
  name: string;
  /** Required for OTHER partners; must be unique across all referral partners. */
  mobileNumber?: string;
  organization?: string;
  designation?: string;
  employeeId?: string;
  /** Required and greater than 0. */
  defaultSharePercent: number;
}

export interface ReferralFilters {
  search?: string;
  /** 'DOCTOR' | 'EMPLOYEE' | 'OTHER'. */
  type?: string;
  isActive?: string | boolean;
}

export interface ReferralListResponse {
  meta: PaginationMeta;
  stats?: { trashed?: number };
}

export const referralService = {
  getReferralPartners: (
    page = 1,
    limit = 20,
    filters?: ReferralFilters,
  ): Promise<ApiResponse<ReferralPartner[]> & ReferralListResponse> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters?.search) params.set('search', filters.search);
    if (filters?.type) params.set('type', filters.type);
    if (filters?.isActive !== undefined) params.set('isActive', String(filters.isActive));
    return get<ReferralPartner[]>(`/referrals?${params.toString()}`) as Promise<
      ApiResponse<ReferralPartner[]> & ReferralListResponse
    >;
  },

  /** Lightweight active-partner feed for the receipt combobox. */
  searchReferralPartners: (q?: string, limit = 25, type?: ReferralPartnerType | 'ALL'): Promise<ApiResponse<ReferralPartner[]>> => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (q) params.set('q', q);
    if (type && type !== 'ALL') params.set('type', type);
    return get<ReferralPartner[]>(`/referrals/search?${params.toString()}`);
  },

  getReferralPartner: (id: string): Promise<ApiResponse<ReferralPartner>> =>
    get<ReferralPartner>(`/referrals/${id}`),

  findByEmployee: (employeeId: string): Promise<ApiResponse<ReferralPartner | null>> =>
    get<ReferralPartner | null>(`/referrals/by-employee/${employeeId}`),

  getNextCode: (): Promise<ApiResponse<{ code: string }>> =>
    get<{ code: string }>('/referrals/next-code'),

  getTrashed: (
    page = 1,
    limit = 20,
    search?: string,
  ): Promise<ApiResponse<ReferralPartner[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    return get<ReferralPartner[]>(`/referrals/trash?${params.toString()}`) as Promise<
      ApiResponse<ReferralPartner[]> & { meta: PaginationMeta }
    >;
  },

  getSummary: (id: string): Promise<ApiResponse<ReferralSummary>> =>
    get<ReferralSummary>(`/referrals/${id}/summary`),

  getPayouts: (
    id: string,
    page = 1,
    limit = 20,
  ): Promise<ApiResponse<ReferralPayout[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    return get<ReferralPayout[]>(`/referrals/${id}/payouts?${params.toString()}`) as Promise<
      ApiResponse<ReferralPayout[]> & { meta: PaginationMeta }
    >;
  },

  createReferralPartner: (data: Partial<ReferralPartner>): Promise<ApiResponse<ReferralPartner>> =>
    post<ReferralPartner>('/referrals', data),

  /** Idempotent quick-create used inline by the OPD receipt form. */
  quickCreate: (data: QuickReferralPartnerInput): Promise<ApiResponse<ReferralPartner>> =>
    post<ReferralPartner>('/referrals/quick', data),

  updateReferralPartner: (
    id: string,
    data: Partial<ReferralPartner>,
  ): Promise<ApiResponse<ReferralPartner>> => patch<ReferralPartner>(`/referrals/${id}`, data),

  deleteReferralPartner: (id: string): Promise<ApiResponse<void>> => del<void>(`/referrals/${id}`),

  /** Bulk soft-delete — moves the selected partners to the trash. */
  bulkTrash: (
    ids: string[],
  ): Promise<ApiResponse<{ trashed: string[]; notFound: string[] }>> =>
    post('/referrals/bulk-trash', { ids }),

  restore: (ids: string[]): Promise<ApiResponse<{ restored: string[]; notFound: string[] }>> =>
    post('/referrals/trash/bulk-restore', { ids }),

  permanentDelete: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> =>
    post('/referrals/trash/bulk-delete', { ids }),

  // ── Payouts (referral-partner accruals + printable vouchers) ──

  getPayoutSummary: (params?: {
    status?: string;
    referralId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<ReferralPayoutSummaryRow[]> & { meta: PaginationMeta; stats?: { totals: ReferralPayoutTotals } }> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.referralId) qs.set('referralId', params.referralId);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return get<ReferralPayoutSummaryRow[]>(`/billing/referral-payouts/summary?${qs.toString()}`) as Promise<
      ApiResponse<ReferralPayoutSummaryRow[]> & { meta: PaginationMeta; stats?: { totals: ReferralPayoutTotals } }
    >;
  },

  getPayoutVouchers: (params?: {
    referralId?: string;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<ReferralPayoutVoucher[]> & { meta: PaginationMeta; stats?: { totalAmount: number } }> => {
    const qs = new URLSearchParams();
    if (params?.referralId) qs.set('referralId', params.referralId);
    if (params?.dateFrom) qs.set('dateFrom', params.dateFrom);
    if (params?.dateTo) qs.set('dateTo', params.dateTo);
    if (params?.search) qs.set('search', params.search);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    return get<ReferralPayoutVoucher[]>(`/billing/referral-payouts?${qs.toString()}`) as Promise<
      ApiResponse<ReferralPayoutVoucher[]> & { meta: PaginationMeta; stats?: { totalAmount: number } }
    >;
  },

  getPayout: (id: string): Promise<ApiResponse<ReferralPayoutVoucher>> =>
    get<ReferralPayoutVoucher>(`/billing/referral-payouts/${id}`),

  /** Fetch the voucher PDF as an object URL for print / download. */
  printPayout: async (id: string): Promise<string> => {
    const response = await httpService.get(`/billing/referral-payouts/${id}/print`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    return URL.createObjectURL(blob);
  },

  /**
   * Settle accrued referral shares into numbered payout vouchers.
   *
   * Routed through the dedicated referral endpoint so referral payouts stay the
   * single source for paying partners. Payouts are Billing-scoped, so the call
   * is authorized by `billing:settlement:settle` (the same family that guards
   * `/billing/payouts/settle`).
   */
  settlePayouts: (data: {
    referralId: string;
    dateTo?: string;
    method?: string;
    notes?: string;
  }): Promise<ApiResponse<{ settled: number; payments: any[] }>> =>
    post<{ settled: number; payments: any[] }>('/billing/referral-payouts/settle', data),
};

export interface ReferralPayoutSummaryRow {
  referralId: string | null;
  referralName: string;
  referralCode: string;
  mobileNumber: string;
  totalAmount: number;
  share: number;
  pendingShare: number;
  postedShare: number;
  settledShare: number;
  outstandingShare: number;
}

export interface ReferralPayoutTotals {
  totalAmount: number;
  share: number;
  outstandingShare: number;
  settledShare: number;
}

export interface ReferralPayoutVoucher {
  id: string;
  paymentNumber: string;
  payeeType?: string;
  payeeNameSnapshot?: string;
  referralId?: string | null;
  amount: number;
  method?: string;
  status: string;
  paidAt: string;
  distributionCount?: number;
  notes?: string;
  externalReference?: string;
  isOpening?: boolean;
  referral?: {
    id: string;
    name: string;
    code?: string;
    mobileNumber?: string;
    designation?: string;
    organization?: string;
    bankName?: string;
    bankAccountNumber?: string;
  } | null;
  paidBy?: { firstName?: string; lastName?: string } | null;
  /** Settled line items, present on the single-voucher detail endpoint. */
  distributions?: ReferralPayoutDistribution[];
}

/** A single settled referral-share line shown on a payout receipt. */
export interface ReferralPayoutDistribution {
  id: string;
  sourceType?: 'DIAGNOSTIC' | 'CONSULTATION' | 'ADMISSION' | string;
  totalAmount?: number;
  doctorShare?: number;
  doctorPercentage?: number;
  receiptItem?: {
    testName?: string;
    test?: { name?: string } | null;
    receipt?: { receiptNumber?: string; createdAt?: string } | null;
  } | null;
  /** Flat IPD commission accruals carry the admission instead of a receipt. */
  admission?: {
    id: string;
    admittedAt?: string;
    patient?: { id?: string; firstName?: string; lastName?: string } | null;
  } | null;
  appointment?: { appointmentType?: string; date?: string } | null;
}
